import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Database, HardDrive, KeyRound, Loader2, Lock, LogOut, Moon, ShieldCheck, Sun } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useAuth } from '../contexts/AuthContext';
import { useStudents } from '../contexts/StudentsContext';
import { useTheme } from '../contexts/ThemeContext';
import { initialsOf } from '../utils/format';
import { scorePassword } from '../utils/security';
export function Settings() {
  const {
    user,
    logout,
    lock,
    changeMasterPassword
  } = useAuth();
  const {
    students
  } = useStudents();
  const {
    theme,
    setTheme
  } = useTheme();
  const navigate = useNavigate();
  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const displayName = user?.displayName || user?.email || 'Signed in';
  const withPhotos = students.filter(student => Boolean(student.photoURL)).length;
  const handleChangePassword = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setError('');
    if (next.length < 6) {
      setError('The new master password needs at least 6 characters.');
      return;
    }
    if (next !== confirm) {
      setError('The new passwords do not match.');
      return;
    }
    setSaving(true);
    try {
      await changeMasterPassword(current, next);
      toast.success('Master password updated');
      setCurrent('');
      setNext('');
      setConfirm('');
    } catch (changeError) {
      setError(changeError instanceof Error ? changeError.message : 'Could not update the master password.');
    } finally {
      setSaving(false);
    }
  };
  return <div className="mx-auto w-full max-w-4xl space-y-5" data-id="element-627" data-mp="2hl6uexwox4d8">
      <section className="glass flex flex-col gap-4 rounded-3xl p-6 sm:flex-row sm:items-center" data-id="element-628" data-mp="3789klrx5mqd7">
        {user?.photoURL ? <img src={user.photoURL} alt={displayName} className="h-16 w-16 rounded-2xl border border-[var(--glass-border)] object-cover" data-id="element-629" data-mp="3b9ldq7f8ltq5" /> : <span className="grid h-16 w-16 place-items-center rounded-2xl brand-gradient text-lg font-semibold text-white" data-id="element-630" data-mp="3l82axm4rr4kn">
            {initialsOf(displayName)}
          </span>}
        <div className="min-w-0 flex-1" data-id="element-631" data-mp="3u6w1ovvf50qa">
          <h2 className="truncate text-lg font-semibold tracking-tight text-foreground" data-id="element-632" data-mp="3u5kwcrmbrkx2">{displayName}</h2>
          <p className="truncate text-sm text-muted-foreground" data-id="element-633" data-mp="2vm8sgaaey0i">{user?.email}</p>
          <p className="mt-1.5 inline-flex items-center gap-1.5 rounded-full bg-emerald-500/12 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:text-emerald-300" data-id="element-634" data-mp="7rl4yq1uxkbh">
            <ShieldCheck className="h-3 w-3" data-id="element-635" data-mp="hrbxim6uxp8x" />
            Google account verified
          </p>
        </div>
        <div className="flex flex-col gap-2 sm:flex-row" data-id="element-636" data-mp="22a5y7ue3c1o4">
          <Button variant="outline" className="h-9 rounded-xl" onClick={() => {
          lock();
          navigate('/security', {
            replace: true
          });
        }} data-id="element-637" data-mp="1x8yqtwfv14hz">
            <Lock className="mr-1.5 h-4 w-4" data-id="element-638" data-mp="1ncdj33cghfag" />
            Lock app
          </Button>
          <Button variant="ghost" className="h-9 rounded-xl text-destructive" onClick={async () => {
          await logout();
          toast.success('Signed out safely.');
          navigate('/', {
            replace: true
          });
        }} data-id="element-639" data-mp="2kzc94v8n7ozd">
            <LogOut className="mr-1.5 h-4 w-4" data-id="element-640" data-mp="26apvqz2kt7as" />
            Sign out
          </Button>
        </div>
      </section>

      <div className="grid gap-4 lg:grid-cols-2" data-id="element-641" data-mp="33qlty0nv6069">
        <section className="glass rounded-3xl p-6" data-id="element-642" data-mp="33rew0obvo1i2">
          <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-643" data-mp="2yy25u9rjvmn7">Appearance</h3>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-644" data-mp="2jfsrbdb95vs9">Choose how Student Recorder looks on this device.</p>
          <div className="mt-4 grid grid-cols-2 gap-3" data-id="element-645" data-mp="2ogoyq0utc02f">
            {(['light', 'dark'] as const).map(option => <button key={option} type="button" onClick={() => setTheme(option)} aria-pressed={theme === option} className={`flex flex-col items-start gap-3 rounded-2xl border p-4 text-left transition-all ${theme === option ? 'border-brand bg-white/70 shadow-sm dark:bg-white/10' : 'border-[var(--glass-hairline)] hover:bg-white/50 dark:hover:bg-white/5'}`} data-id="element-646" data-mp="29khex1nbh0no">
                <span className={`grid h-9 w-9 place-items-center rounded-xl ${option === 'dark' ? 'bg-slate-900 text-slate-100' : 'bg-amber-100 text-amber-700'}`} data-id="element-647" data-mp="3l6g26pl30xkd">
                  {option === 'dark' ? <Moon className="h-4 w-4" data-id="element-648" data-mp="1eg2zm14fcydz" /> : <Sun className="h-4 w-4" data-id="element-649" data-mp="19iucrn8c066e" />}
                </span>
                <span className="text-sm font-medium capitalize text-foreground" data-id="element-650" data-mp="19ikn60r78118">{option} mode</span>
              </button>)}
          </div>
        </section>

        <section className="glass rounded-3xl p-6" data-id="element-651" data-mp="1j9p1cy91vhv0">
          <h3 className="text-sm font-semibold tracking-tight text-foreground" data-id="element-652" data-mp="vj1qq57r2e4r">Firebase connection</h3>
          <p className="mt-1 text-xs text-muted-foreground" data-id="element-653" data-mp="qjylokrrs7ws">Project student-recorder-web-app</p>
          <ul className="mt-4 space-y-3 text-sm" data-id="element-654" data-mp="14jrj0yxsxmdq">
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-655" data-mp="14kj4vi1lk5qt">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-amber-500/12 text-amber-600" data-id="element-656" data-mp="10gl4l40jcow6">
                <Database className="h-4 w-4" data-id="element-657" data-mp="2fx3fgor8f2pv" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-658" data-mp="2bw9r5u9vnlwg">
                <span className="block text-sm font-medium text-foreground" data-id="element-659" data-mp="2bznup5ua215h">Cloud Firestore</span>
                <span className="block truncate text-xs text-muted-foreground" data-id="element-660" data-mp="18oi83j7zp20n">
                  <span className="font-mono" data-id="element-661" data-mp="1djnoy3mjbqhv">students</span> · {students.length} document
                  {students.length === 1 ? '' : 's'}
                </span>
              </span>
            </li>
            <li className="flex items-center gap-3 rounded-2xl border border-[var(--glass-hairline)] p-3" data-id="element-662" data-mp="1mldra4hyg9uf">
              <span className="grid h-9 w-9 place-items-center rounded-xl bg-sky-500/12 text-sky-600" data-id="element-663" data-mp="pttdnqhv69zq">
                <HardDrive className="h-4 w-4" data-id="element-664" data-mp="uswlfip8aube" />
              </span>
              <span className="min-w-0 flex-1" data-id="element-665" data-mp="yvsx0pgcpq5u">
                <span className="block text-sm font-medium text-foreground" data-id="element-666" data-mp="13nawta6n1kp8">Firebase Storage</span>
                <span className="block truncate text-xs text-muted-foreground" data-id="element-667" data-mp="75emn37jqvyx">
                  <span className="font-mono" data-id="element-668" data-mp="73bwmj6e92u7">student_photos/</span> · {withPhotos} photo{withPhotos === 1 ? '' : 's'}
                </span>
              </span>
            </li>
          </ul>
        </section>
      </div>

      <section className="glass rounded-3xl p-6" data-id="element-669" data-mp="3utsajuhfjhid">
        <h3 className="flex items-center gap-2 text-sm font-semibold tracking-tight text-foreground" data-id="element-670" data-mp="3uubkanwxezlb">
          <KeyRound className="h-4 w-4 text-brand" data-id="element-671" data-mp="3qqncq58ao402" />
          Master password
        </h3>
        <p className="mt-1 text-xs text-muted-foreground" data-id="element-672" data-mp="3lsc23sa6dpgs">
          Only a salted SHA-256 digest is stored in Firestore. Changing it keeps this device unlocked.
        </p>

        <form onSubmit={handleChangePassword} className="mt-5 grid gap-4 sm:grid-cols-3" noValidate data-id="element-673" data-mp="3c328g7zonyqk">
          <div className="space-y-2" data-id="element-674" data-mp="373fyq8j2d0mi">
            <Label htmlFor="current-master" data-id="element-675" data-mp="374h7g6clxb12">Current password</Label>
            <Input id="current-master" type="password" value={current} onChange={event => setCurrent(event.target.value)} autoComplete="current-password" className="h-10 rounded-xl" data-id="element-676" data-mp="333nj5bv95u7n" />
          </div>
          <div className="space-y-2" data-id="element-677" data-mp="kwt8ub01dx3v">
            <Label htmlFor="new-master" data-id="element-678" data-mp="ktniytjl2r73">New password</Label>
            <Input id="new-master" type="password" value={next} onChange={event => setNext(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-679" data-mp="pts3cunm462l" />
            {next.length > 0 && <p className="text-xs text-muted-foreground" data-id="element-680" data-mp="247exw8owglx">Strength: {scorePassword(next).label}</p>}
          </div>
          <div className="space-y-2" data-id="element-681" data-mp="6xa4ltacj5wu">
            <Label htmlFor="confirm-master" data-id="element-682" data-mp="3pa845z387wqh">Confirm new password</Label>
            <Input id="confirm-master" type="password" value={confirm} onChange={event => setConfirm(event.target.value)} autoComplete="new-password" className="h-10 rounded-xl" data-id="element-683" data-mp="3p856qtzqv1bq" />
          </div>

          {error && <p role="alert" className="rounded-xl bg-destructive/10 px-3 py-2 text-sm font-medium text-destructive sm:col-span-3" data-id="element-684" data-mp="30otpwdcdkf82">
              {error}
            </p>}

          <div className="sm:col-span-3" data-id="element-685" data-mp="2vsd1rj56e0uz">
            <Button type="submit" disabled={saving} className="h-10 rounded-xl brand-gradient px-5 text-white hover:opacity-95" data-id="element-686" data-mp="3anj7e50czxns">
              {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" data-id="element-687" data-mp="3jn61e5qre16t" />}
              {saving ? 'Updating…' : 'Update master password'}
            </Button>
          </div>
        </form>
      </section>
    </div>;
}